
import { useState } from 'react';

export default function Pricing() {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const plans = [
    {
      name: 'DIGITAL FOUNDATION',
      monthlyPrice: '350',
      pricePrefix: '',
      valueDescriptor: 'Essential online presence',
      launchLabel: 'Includes one-time launch',
      launchCost: '2,500',
      launchTooltip: 'One-Time Launch: $2,500\nCovers website setup, SEO foundation, hosting, and onboarding.\nPaid once at activation.',
      features: [
        'Professional 5-Page Website',
        'Mobile-Friendly Design',
        'Core SEO & "Near Me" Optimization',
        'Hosting & Security Included',
        'Google Business Profile Setup',
        'Monthly GBP Health Check',
        'Quarterly Performance Report'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'GROWTH ACCELERATOR',
      monthlyPrice: '895',
      pricePrefix: '',
      valueDescriptor: 'Managed growth & automation',
      launchLabel: 'Managed launch included',
      launchCost: '4,500',
      launchTooltip: 'One-Time Launch: $4,500\nCovers setup, integrations, SEO foundation, automation, and onboarding.\nPaid once at activation.',
      baseFeatures: 'Everything in Digital Foundation, plus:',
      features: [
        'Appointment Booking System Integration',
        'Automated Review Generation',
        'Active GBP Management (2-4 Posts/Month)',
        'Social Media Light (1 Post/Week)',
        'Advanced Local SEO Optimization',
        '15% Off Marketing Materials',
        'Monthly 15-Min Growth Sync Call'
      ],
      cta: 'Start Growing',
      popular: true
    },
    {
      name: 'MARKET LEADER',
      monthlyPrice: '2,000',
      pricePrefix: 'From',
      valueDescriptor: 'Advanced digital marketing',
      launchLabel: 'Full custom activation',
      launchCost: 'from 7,500',
      launchTooltip: 'Full Activation: from $7,500\nCustom build, integrations, multi-location setup, and advanced systems.\nPaid once at activation.',
      baseFeatures: 'Everything in Growth Accelerator, plus:',
      features: [
        'Full Custom Website (Unlimited Pages)',
        'Custom Landing Pages Per City',
        'Google Ads (PPC) or LSA Management',
        'Monthly Blog Posts or Videos',
        'Full Social Management (3-5 Posts/Week)',
        '25% Off All Marketing Materials',
        'Advanced Analytics & ROI Tracking',
        'Dedicated Account Manager'
      ],
      cta: 'Schedule Consultation',
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Choose Your <strong className="text-[#0F3A2E]">Service Tier</strong>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Flexible programs designed to meet you where you are and take you where you want to go
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          {plans.map((plan, index) => (
            <article
              key={index}
              className={`rounded-2xl relative transition-all bg-white ${
                plan.popular 
                  ? 'shadow-2xl border-2 border-[#0F3A2E] md:scale-105' 
                  : 'shadow-lg border border-gray-200 hover:shadow-xl'
              }`}
              style={plan.popular ? { backgroundColor: '#0A2A20' } : {}}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap shadow-[0_4px_12px_rgba(201,162,77,0.4)] transform hover:scale-105 transition-transform" style={{ backgroundColor: '#C9A24D', color: '#1F2937', boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 4px 12px rgba(201, 162, 77, 0.4)' }}>
                    RECOMMENDED
                  </span>
                </div>
              )}

              <div className="p-8">
                <h3 className={`text-lg font-bold mb-6 tracking-widest ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>

                <div className="mb-8 min-h-[180px] flex flex-col">
                  <div className="flex-grow">
                    {plan.pricePrefix && (
                      <div className={`text-2xl font-medium mb-1 tracking-wide ${plan.popular ? 'text-gray-300' : 'text-[#6F847B]'}`}>
                        {plan.pricePrefix}
                      </div>
                    )}
                    <div className="flex items-baseline justify-start mb-3">
                      <span className={`text-6xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                        ${plan.monthlyPrice}
                      </span>
                      <span className={`ml-2 text-xl ${plan.popular ? 'text-gray-300' : 'text-gray-500'}`}>/month</span>
                    </div>
                    <p className={`text-sm mb-4 ${plan.popular ? 'text-gray-300' : 'text-gray-600'}`}>
                      {plan.valueDescriptor}
                    </p>

                    <div className="relative inline-block">
                      <button
                        onMouseEnter={() => setActiveTooltip(index)}
                        onMouseLeave={() => setActiveTooltip(null)}
                        onClick={() => setActiveTooltip(activeTooltip === index ? null : index)}
                        className={`flex items-center text-sm transition-colors cursor-pointer ${
                          plan.popular 
                            ? 'text-gray-300 hover:text-white' 
                            : 'text-gray-600 hover:text-[#0F3A2E]'
                        }`}
                      >
                        <i className="ri-information-line text-base mr-1"></i>
                        <span className="underline decoration-dotted">{plan.launchLabel}</span>
                      </button>
                      
                      {activeTooltip === index && (
                        <div className="absolute left-0 top-full mt-2 w-72 bg-gray-800 text-white text-xs rounded-lg p-4 shadow-xl z-10 animate-fadeIn">
                          {plan.launchTooltip.split('\n').map((line, i) => (
                            <p key={i} className={i > 0 ? 'mt-2' : ''}>
                              {line}
                            </p>
                          ))}
                          <div className="absolute -top-2 left-6 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => scrollToSection('contact')}
                    className={`w-full py-4 px-6 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer text-base mt-auto transform hover:scale-105 ${
                      plan.popular
                        ? 'text-gray-900 shadow-[0_4px_12px_rgba(201,162,77,0.4)]'
                        : 'bg-[#0F3A2E] text-white hover:bg-[#0A2A20] shadow-lg'
                    }`}
                    style={plan.popular ? { 
                      backgroundColor: '#C9A24D',
                      boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 4px 12px rgba(201, 162, 77, 0.4)'
                    } : {}}
                  >
                    {plan.cta}
                  </button>
                </div>

                {plan.baseFeatures && (
                  <div className={`mb-4 pb-4 border-b ${plan.popular ? 'border-gray-600' : 'border-gray-200'}`}>
                    <p className={`text-sm font-semibold ${plan.popular ? 'text-gray-200' : 'text-[#0F3A2E]'}`}>
                      {plan.baseFeatures}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start">
                      <div className={`w-5 h-5 flex items-center justify-center mr-3 flex-shrink-0 ${
                        plan.popular ? 'text-[#C9A24D]' : 'text-[#0F3A2E]'
                      }`}>
                        <i className="ri-check-line text-lg"></i>
                      </div>
                      <span className={`text-sm leading-relaxed ${plan.popular ? 'text-gray-200' : 'text-gray-700'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-gray-600 max-w-3xl mx-auto leading-relaxed">
            All tiers include professional support and are designed specifically for HVAC businesses. Custom solutions available for unique needs.
          </p>
        </div>
      </div>
    </section>
  );
}
